import { createStore } from "./createStore";
import type {
  DeepPartial,
  SettingsSnapshot,
  AgentModesCatalog,
  AgentUpdatesSnapshot,
  AgentUpdateEvent,
} from "../api/client";
import { api } from "../api/client";

interface SettingsState {
  loaded: boolean;
  settings: SettingsSnapshot;
  agentModes: AgentModesCatalog;
  error: string | null;
  updates: AgentUpdatesSnapshot;
  installing: Record<string, AgentUpdateEvent[]>;
  installResult: Record<
    string,
    { state: "success" | "failure"; installed: string | null; exitCode?: number } | undefined
  >;
}

const INITIAL_SETTINGS: SettingsSnapshot = {
  agents: {},
  appearance: { theme: "dark" },
  notifications: {
    enabled: true,
    triggers: { "response-ready": true, "cli-idle": true, "approval-needed": true },
    chime: true,
  },
};

export const settingsStore = createStore<SettingsState>({
  loaded: false,
  settings: INITIAL_SETTINGS,
  agentModes: {},
  error: null,
  updates: { lastCheckAt: null, snapshot: {} },
  installing: {},
  installResult: {},
});

export async function loadSettings(): Promise<void> {
  try {
    const [settings, agentModes] = await Promise.all([api.getSettings(), api.getAgentModes()]);

    // One-time migration: lift legacy localStorage["agentguard-theme"] onto
    // the server settings if the server is still at its default dark theme.
    // Idempotent: after the first run the localStorage key is cleared.
    const legacyTheme = (() => {
      try {
        return localStorage.getItem("agentguard-theme");
      } catch {
        return null;
      }
    })();
    // Only "light" is a known legacy value worth migrating. Anything else
    // (unknown strings like "system" / "auto" / garbage) is left alone and
    // the localStorage key is cleared anyway so the migration doesn't re-fire.
    if (legacyTheme && settings.appearance.theme === "dark") {
      if (legacyTheme === "light") {
        await api.updateSettings({ appearance: { theme: "light" } });
        settings.appearance.theme = "light";
      }
      try {
        localStorage.removeItem("agentguard-theme");
      } catch {
        /* hardened webview */
      }
    }

    settingsStore.setState({ loaded: true, settings, agentModes, error: null });
  } catch (e) {
    settingsStore.setState({ error: e instanceof Error ? e.message : String(e) });
  }
}

export async function updateSettings(patch: DeepPartial<SettingsSnapshot>): Promise<void> {
  const next = await api.updateSettings(patch);
  settingsStore.setState((prev) => ({ ...prev, settings: next }));
}

export async function setAgentMode(agentId: string, modeId: string): Promise<void> {
  const next = await api.setAgentMode(agentId, modeId);
  settingsStore.setState((prev) => ({ ...prev, settings: next }));
}

export async function loadAgentUpdates(): Promise<void> {
  try {
    const snap = await api.getAgentUpdates();
    settingsStore.setState((prev) => ({ ...prev, updates: snap }));
  } catch {
    // Version-check failures are expected (e.g. agent not installed). Don't surface in global error banner.
  }
}

export async function checkAgentUpdates(force = false): Promise<void> {
  try {
    const snap = await api.checkAgentUpdates(force);
    settingsStore.setState((prev) => ({ ...prev, updates: snap }));
  } catch {
    // Version-check failures are expected (e.g. agent not installed). Don't surface in global error banner.
  }
}

export function installAgentUpdate(agentId: string): Promise<string | null> {
  // Clear prior log buffer + result for this agent.
  settingsStore.setState((prev) => ({
    ...prev,
    installing: { ...prev.installing, [agentId]: [] },
    installResult: { ...prev.installResult, [agentId]: undefined },
  }));

  return new Promise<string | null>((resolve, reject) => {
    let installed: string | null = null;
    // Accumulate all events in a local buffer to avoid O(N²) array spreading in setState.
    // Log events are flushed at most every 100ms; non-log events flush immediately.
    const buffer: AgentUpdateEvent[] = [];
    let logFlushTimer: ReturnType<typeof setTimeout> | null = null;

    function flush() {
      if (logFlushTimer !== null) {
        clearTimeout(logFlushTimer);
        logFlushTimer = null;
      }
      settingsStore.setState((prev) => ({
        ...prev,
        installing: { ...prev.installing, [agentId]: buffer.slice() },
      }));
    }

    api.installAgentUpdate(
      agentId,
      (event) => {
        buffer.push(event);
        if (event.type === "log") {
          // Debounce state updates for log bursts to avoid O(N²) array copies.
          if (logFlushTimer === null) {
            logFlushTimer = setTimeout(flush, 100);
          }
        } else {
          // Non-log events flush the buffer immediately.
          flush();
        }

        if (event.type === "done") {
          installed = event.installed;
          // Update installResult with the resolved version (status fires before done).
          settingsStore.setState((prev) => {
            const prior = prev.installResult[agentId];
            if (prior?.state === "success") {
              return {
                ...prev,
                installResult: { ...prev.installResult, [agentId]: { ...prior, installed } },
              };
            }
            return prev;
          });
        }
        if (event.type === "status" && (event.state === "success" || event.state === "failure")) {
          const state = event.state;
          settingsStore.setState((prev) => ({
            ...prev,
            installResult: {
              ...prev.installResult,
              [agentId]: { state, installed, exitCode: event.exitCode },
            },
          }));
        }
      },
      async () => {
        // Stream ended cleanly. Flush any remaining buffered events, then refresh snapshot.
        flush();
        try {
          await loadAgentUpdates();
        } catch {
          /* non-fatal */
        }
        resolve(installed);
      },
      (err) => {
        flush();
        // Record failure result so the row can show "Failed" even on transport error.
        settingsStore.setState((prev) => ({
          ...prev,
          installResult: {
            ...prev.installResult,
            [agentId]: { state: "failure", installed: null },
          },
        }));
        reject(err);
      }
    );
  });
}
